import React from 'react'
import * as types from '../types'
import * as stores from '../stores/appStore'
import EditPersonModal from './EditPersonModal'

interface PersonCardProps {
  person: types.Person
}

const PersonCard: React.FC<PersonCardProps> = ({ person }) => {
  const { syncPerson, deletePerson, groups } = stores.useAppStore()
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isSyncing, setIsSyncing] = React.useState(false)
  const [showMenu, setShowMenu] = React.useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSync = async () => {
    setIsSyncing(true)
    await syncPerson(person.id)
    setIsSyncing(false)
  }

  const handleDelete = async () => {
    if (confirm(`Delete ${person.firstName} ${person.lastName}? This cannot be undone.`)) {
      await deletePerson(person.id)
    }
    setShowMenu(false)
  }

  // Calculate total available slots
  const totalSlots = React.useMemo(() => {
    return person.calendars.reduce((total, calendar) => {
      return total + (calendar.cachedAvailability?.slots.length || 0)
    }, 0)
  }, [person.calendars])

  // Get person's groups
  const personGroups = React.useMemo(() => {
    return groups.filter(g => person.groups.includes(g.id))
  }, [groups, person.groups])

  return (
    <div className="bg-card rounded-lg border border-border p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          {/* Avatar */}
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: person.color || '#3b82f6' }}
          >
            {person.avatarUrl ? (
              <img src={person.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              <span>
                {person.firstName[0]}{person.lastName?.[0] || ''}
              </span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-lg">
                {person.firstName} {person.lastName}
              </h3>
              {person.email && (
                <span className="text-sm text-gray-500">{person.email}</span>
              )}
            </div>

            <div className="mt-1 flex items-center space-x-4 text-sm">
              <span className="flex items-center text-gray-600">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                {person.calendars.length} calendar{person.calendars.length !== 1 && 's'}
              </span>
              {totalSlots > 0 && (
                <span className="text-gray-600">
                  • {totalSlots} slots next 7 days
                </span>
              )}
              {person.lastSyncedAt ? (
                <span className="flex items-center text-green-600">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Synced
                </span>
              ) : person.calendars.some(c => c.error) ? (
                <span className="flex items-center text-red-600">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Failed to fetch
                </span>
              ) : (
                <span className="text-gray-500">Not synced</span>
              )}
            </div>

            {/* Groups */}
            {personGroups.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {personGroups.map(group => (
                  <span
                    key={group.id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                  >
                    {group.emoji} {group.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="View Slots"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>

          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Sync Calendars"
          >
            {isSyncing ? (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
          </button>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="More Options"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <button
                  onClick={() => {
                    setIsEditModalOpen(true)
                    setShowMenu(false)
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Person
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded View - Show calendars and slots */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-border">
          <h4 className="font-semibold mb-2">Calendars</h4>
          <div className="space-y-3">
            {person.calendars.map(calendar => (
              <div key={calendar.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full ${calendar.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <span className="font-medium text-gray-700">{calendar.name || 'Unnamed Calendar'}</span>
                    <span className="text-gray-500 text-xs bg-gray-200 px-2 py-1 rounded">{calendar.provider}</span>
                  </div>
                  {calendar.error && (
                    <span className="text-red-500 text-xs">{calendar.error}</span>
                  )}
                </div>

                {/* Show calendar details */}
                <div className="mt-2 text-xs text-gray-600">
                  <div>URL: {calendar.url}</div>
                  {calendar.lastFetchedAt && (
                    <div>Last synced: {new Date(calendar.lastFetchedAt).toLocaleString()}</div>
                  )}
                  {calendar.cachedAvailability && (
                    <div className="mt-1">
                      <span className="font-semibold">Available slots: {calendar.cachedAvailability.slots.length}</span>
                      {calendar.cachedAvailability.slots.length > 0 && (
                        <div className="mt-1 max-h-24 overflow-y-auto">
                          {calendar.cachedAvailability.slots.slice(0, 5).map((slot, idx) => (
                            <div key={idx} className="text-gray-500">
                              {slot.date} at {new Date(slot.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          ))}
                          {calendar.cachedAvailability.slots.length > 5 && (
                            <div className="text-gray-400 italic">
                              ...and {calendar.cachedAvailability.slots.length - 5} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {person.calendars.length === 0 && (
              <div className="text-gray-500 text-sm">No calendars added yet</div>
            )}
          </div>
        </div>
      )}

      {/* Edit Person Modal */}
      {isEditModalOpen && (
        <EditPersonModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          person={person}
        />
      )}
    </div>
  )
}

export default PersonCard